import {
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { campaignQueryKeys } from "./queryKeys";

import { segmentsQueryKeys } from "@/features/segments/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { UpdateCampaignRequest } from "@/types/request/UpdateCampaignRequest";
import {
  CampaignType,
  CampaignTypeDTO,
} from "@/types/response/CampaignType";
import { PagedResponse } from "@/types/response/PagedResponse";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

type UpdateCampaignContext = {
  previousDetail?: CampaignType;
  previousInfiniteEntries: Array<
    [readonly unknown[], InfiniteData<PagedResponse<CampaignTypeDTO>> | undefined]
  >;
};

export function useUpdateCampaignMutation(clientId?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    CampaignTypeDTO,
    AxiosError<ValidationErrorResponse>,
    UpdateCampaignRequest,
    UpdateCampaignContext
  >({
    mutationFn: (payload) =>
      instance
        .put(`/api/v2/Campaigns/${payload.id}`, payload)
        .then((res) => res.data),
    onMutate: async (payload) => {
      const detailKey = campaignQueryKeys.detail(payload.id, clientId);

      await Promise.all([
        queryClient.cancelQueries({ queryKey: detailKey }),
        queryClient.cancelQueries({
          queryKey: campaignQueryKeys.infinites(),
        }),
      ]);

      const previousDetail =
        queryClient.getQueryData<CampaignType>(detailKey);

      if (previousDetail) {
        queryClient.setQueryData<CampaignType>(detailKey, {
          ...previousDetail,
          name: payload.name,
          segmentId: payload.segmentId,
        });
      }

      const previousInfiniteEntries = queryClient.getQueriesData<
        InfiniteData<PagedResponse<CampaignTypeDTO>>
      >({ queryKey: campaignQueryKeys.infinites() });

      queryClient.setQueriesData<
        InfiniteData<PagedResponse<CampaignTypeDTO>>
      >({ queryKey: campaignQueryKeys.infinites() }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((c) =>
              c.id === payload.id
                ? { ...c, name: payload.name, segmentId: payload.segmentId }
                : c
            ),
          })),
        };
      });

      return { previousDetail, previousInfiniteEntries };
    },
    onError: (_, payload, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(
          campaignQueryKeys.detail(payload.id, clientId),
          context.previousDetail
        );
      }
      for (const [key, data] of context?.previousInfiniteEntries ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.all,
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: segmentsQueryKeys.all,
        refetchType: "all",
      });
    },
  });
}
